import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
    Button,
} from "frontend-components";
import { useTranslation } from "react-i18next";
import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { useCurrentSurvey } from "@pulse/hooks/surveyHooks";
import { deleteSurvey } from "@pulse/api/surveyApi";
import { queryClient } from "@pulse/queryClient";
import { getSurveysOptions } from "@pulse/api/queryApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useSelectedCustomerIdParam } from "@/features/hub/selectedCustomerContext";

export default function DeleteSurveyButton() {
    const { t } = useTranslation();
    const { data: survey } = useCurrentSurvey();
    const navigate = useNavigate();
    const customerId = useSelectedCustomerIdParam();

    const deleteMutation = useMutation({
        mutationFn: ({ surveyId }: { surveyId: number }) => deleteSurvey({ surveyId }),
        onSuccess: () => {
            // Invalidate the surveys list cache to refresh the list
            queryClient.invalidateQueries(
                getSurveysOptions({ pulseInstanceId: survey.pulseInstanceId }),
            );
            // Navigate back to the survey list after successful deletion
            navigate({
                to: "/$customerId/pulse/$pulseId",
                params: { customerId, pulseId: String(survey.pulseInstanceId) },
            });
        },
        onError: (error) => {
            console.error("Error deleting survey:", error);
            // TODO handle error, e.g., show a notification
        },
    });

    return (
        <AlertDialog>
            <AlertDialogTrigger
                render={
                    <Button
                        variant="outline"
                        className="border-chameleon-pink text-chameleon-pink hover:bg-chameleon-pink/10"
                    >
                        {t("survey.deleteDraft")}
                    </Button>
                }
            />
            <AlertDialogContent size="sm">
                <AlertDialogHeader>
                    <FontAwesomeIcon icon={faTrashCan} className="text-chameleon-pink" size="4x" />

                    <AlertDialogTitle>{t("survey.deleteDraft")}</AlertDialogTitle>

                    <AlertDialogDescription>
                        {t("survey.deleteDraftDescription")}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                {deleteMutation.isError && (
                    <div className="text-chameleon-pink text-center">
                        <p>{t("survey.errorDeleteDraftDescription")}</p>
                        <p className="text-xs font-semibold">{deleteMutation.error.message}</p>
                    </div>
                )}
                <AlertDialogFooter>
                    <AlertDialogCancel variant="outline" disabled={deleteMutation.isPending}>
                        {t("common.cancel")}
                    </AlertDialogCancel>
                    <AlertDialogAction
                        variant="fill"
                        onClick={() => {
                            deleteMutation.mutate({ surveyId: survey.id });
                        }}
                        disabled={deleteMutation.isPending}
                    >
                        {t("common.yes")}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
